import process from "node:process";
import { resolveUserFacingCliEntryCommand } from "../runtime/cli-entry-command";
import { resolveAuthBaseUrl } from "./auth-url";
import {
  normalizeGatewayBaseUrl,
  resolveBaseUrl,
  resolveDefaultGatewayBaseUrl,
} from "./base-url";
import type { WakeLoopEnvPreset } from "./paths";
import { resolveWakeLoopEnvPreset } from "./paths";

const WAKELOOP_USER_FACING_CLI_COMMAND_ENV = "WAKELOOP_USER_FACING_CLI_COMMAND";

export type WakeLoopEnvironmentTarget = {
  preset: WakeLoopEnvPreset;
  gatewayUrl: string;
  authBaseUrl: string;
  userFacingCommand: string;
};

type WakeLoopEnvironmentTargetInput = {
  gatewayUrl?: string;
  authUrl?: string;
  env?: NodeJS.ProcessEnv;
};

export async function resolveWakeLoopEnvironmentTarget(
  input: WakeLoopEnvironmentTargetInput = {}
): Promise<WakeLoopEnvironmentTarget> {
  const env = input.env ?? process.env;
  const preset = resolveWakeLoopEnvPreset({ env });
  return {
    preset,
    gatewayUrl: await resolveBaseUrl(input.gatewayUrl, { env }),
    authBaseUrl: resolveAuthBaseUrl(input.authUrl, { env }),
    userFacingCommand: resolveUserFacingCommandForPreset({ env, preset }),
  };
}

export function resolveWakeLoopEnvironmentTargetFromEnv(
  input: WakeLoopEnvironmentTargetInput = {}
): WakeLoopEnvironmentTarget {
  const env = input.env ?? process.env;
  const preset = resolveWakeLoopEnvPreset({ env });
  return {
    preset,
    gatewayUrl: resolveGatewayUrlFromEnvOnly({
      env,
      gatewayUrl: input.gatewayUrl,
    }),
    authBaseUrl: resolveAuthBaseUrl(input.authUrl, { env }),
    userFacingCommand: resolveUserFacingCommandForPreset({ env, preset }),
  };
}

function resolveUserFacingCommandForPreset(input: {
  env: NodeJS.ProcessEnv;
  preset: WakeLoopEnvPreset;
}): string {
  const explicitCommand = normalizeOptionalText(
    input.env[WAKELOOP_USER_FACING_CLI_COMMAND_ENV]
  );
  if (explicitCommand) {
    return explicitCommand;
  }
  if (input.preset === "dev-local") {
    return "wakeloop-dev";
  }
  if (input.preset === "canary") {
    return "wakeloop-canary";
  }
  return resolveUserFacingCliEntryCommand({ env: input.env });
}

function resolveGatewayUrlFromEnvOnly(input: {
  env: NodeJS.ProcessEnv;
  gatewayUrl?: string;
}): string {
  const fromInput = normalizeOptionalText(input.gatewayUrl);
  if (fromInput) {
    return normalizeGatewayBaseUrl(fromInput);
  }
  const fromEnv = normalizeOptionalText(input.env.WAKELOOP_GATEWAY_URL);
  if (fromEnv) {
    return normalizeGatewayBaseUrl(fromEnv);
  }
  return normalizeGatewayBaseUrl(
    resolveDefaultGatewayBaseUrl({ env: input.env })
  );
}

function normalizeOptionalText(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}
